/**
 * Componente base reutilizável para seleção de emojis
 * 
 * Grid de emojis com categorias, busca e configurações flexíveis.
 * Usado tanto para reações em mensagens quanto para inserção de texto.
 * Segue o padrão visual do WhatsApp Web.
 *
 * CHANGELOG:
 *   RELEASE: 2026-08-28 v1.1.0 - Barra de categorias passa a usar ScrollArea horizontal
 *            com barra sempre visível (indica que há mais ícones) e o grid vertical
 *            agora rola por touchpad/roda do mouse (correção em ScrollArea v1.7.0).
 */


import { useState, useCallback } from 'react';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Button } from '@/components/ui/button';
import { Search, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import { EMOJI_CATEGORIES, searchEmojis } from '@/lib/emoji-data';

interface EmojiGridProps {
  /** Callback ao selecionar um emoji */
  onSelect: (emoji: string) => void;
  /** Altura do grid (default: 240px) */
  height?: string;
  /** Número de colunas (default: 8) */
  columns?: number;
  /** Se mostra campo de busca (default: true) */
  showSearch?: boolean;
  /** Se mostra tabs de categorias (default: true) */
  showCategories?: boolean;
  /** Emoji atualmente selecionado (para destacar) */
  selectedEmoji?: string;
  /** Botão extra no footer (ex: "Remover reação") */
  footerAction?: React.ReactNode;
  /** Classes CSS adicionais */
  className?: string;
}

/**
 * EmojiGrid - Componente base para seleção de emojis
 * 
 * Reutilizável em diferentes contextos:
 * - Reações em mensagens (com selectedEmoji e footerAction)
 * - Inserção de texto (sem footer, permite seleção múltipla)
 * - Qualquer outro lugar que precise de seleção de emojis
 */
export function EmojiGrid({
  onSelect,
  height = '240px',
  columns = 8,
  showSearch = true,
  showCategories = true,
  selectedEmoji,
  footerAction,
  className,
}: EmojiGridProps) {
  // Categoria ativa (ID)
  const [activeCategory, setActiveCategory] = useState(EMOJI_CATEGORIES[0].id);
  // Texto de busca
  const [searchQuery, setSearchQuery] = useState('');

  /** Handler para seleção de emoji */
  const handleSelect = useCallback((emoji: string) => {
    onSelect(emoji);
  }, [onSelect]);

  /** Emojis filtrados pela busca ou categoria ativa */
  const displayedEmojis = searchQuery.trim()
    ? searchEmojis(searchQuery)
    : EMOJI_CATEGORIES.find(c => c.id === activeCategory)?.emojis || [];

  /** Gera classes para o grid baseado no número de colunas */
  const gridClasses = `grid gap-0.5`;
  const gridStyle = { gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))` };

  return (
    <div className={cn('flex flex-col', className)}>
      {/* Tabs de categorias — ScrollArea horizontal com barra sempre visível (v1.1.0):
          deixa evidente que existem mais ícones e habilita rolagem por touchpad. */}
      {showCategories && !searchQuery.trim() && (
        <div className="border-b">
          <ScrollArea axis="x" alwaysVisibleScrollbar className="w-full">
            <div className="flex items-center gap-1 px-2 py-1.5 pb-2 w-max">
              {EMOJI_CATEGORIES.map((category) => (
                <Button
                  key={category.id}
                  variant="ghost"
                  size="sm"
                  className={cn(
                    'h-9 w-9 p-0 text-base leading-none flex-shrink-0',
                    activeCategory === category.id && 'bg-muted'
                  )}
                  onClick={() => setActiveCategory(category.id)}
                  title={category.label}
                >
                  {category.icon}
                </Button>
              ))}
            </div>
          </ScrollArea>
        </div>
      )}


      {/* Barra de busca */}
      {showSearch && (
        <div className="p-2 border-b">
          <div className="relative">
            <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Buscar emoji..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-8 h-8 text-sm"
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            )}
          </div>
        </div>
      )}

      {/* Grid de emojis — axis="y" garante scroll vertical limpo, reserveScrollbarSpace evita sobreposição */}
      <ScrollArea style={{ height }} axis="y" reserveScrollbarSpace>
        <div className="p-2">
          {/* Título da categoria (quando não está buscando) */}
          {!searchQuery.trim() && (
            <p className="text-xs text-muted-foreground mb-2 font-medium">
              {EMOJI_CATEGORIES.find(c => c.id === activeCategory)?.label}
            </p>
          )}
          
          {/* Resultado da busca */}
          {searchQuery.trim() && displayedEmojis.length > 0 && (
            <p className="text-xs text-muted-foreground mb-2 font-medium">
              Resultados da busca
            </p>
          )}
          
          <div className={gridClasses} style={gridStyle}>
            {displayedEmojis.map((emoji, index) => (
              <button
                key={`${emoji}-${index}`}
                type="button"
                className={cn(
                  'h-8 w-8 flex items-center justify-center text-xl rounded transition-colors',
                  'hover:bg-muted',
                  selectedEmoji === emoji && 'bg-primary/20 ring-1 ring-primary'
                )}
                onClick={() => handleSelect(emoji)}
                title={emoji}
              >
                {emoji}
              </button>
            ))}
          </div>

          {/* Mensagem quando não há resultados */}
          {displayedEmojis.length === 0 && searchQuery.trim() && (
            <p className="text-sm text-muted-foreground text-center py-4">
              Nenhum emoji encontrado
            </p>
          )}
        </div>
      </ScrollArea>

      {/* Footer opcional (ex: botão remover reação) */}
      {footerAction && (
        <div className="border-t p-2 flex justify-center">
          {footerAction}
        </div>
      )}
    </div>
  );
}
